import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTabActionsOptional } from "../context/tabContext";
import {
  TAB_NAV_ENABLED,
  buildTabLocation,
  normalizePathname,
  normalizeSearch,
} from "../utils/tabNavigation";

export function useTabNavigate() {
  const navigate = useNavigate();
  const tabActions = useTabActionsOptional();

  const enabled = TAB_NAV_ENABLED && tabActions?.enabled;

  const open = useCallback(
    ({ pathname, search = "", label, pinned = false }) => {
      const normalizedPath = normalizePathname(pathname);
      const normalizedSearch = normalizeSearch(search);

      if (enabled) {
        return tabActions.openTab({
          pathname: normalizedPath,
          search: normalizedSearch,
          label,
          pinned,
        });
      }

      navigate(buildTabLocation(normalizedPath, normalizedSearch));
      return null;
    },
    [enabled, navigate, tabActions]
  );

  const replace = useCallback(
    ({ pathname, search = "", label }) => {
      const normalizedPath = normalizePathname(pathname);
      const normalizedSearch = normalizeSearch(search);

      if (enabled) {
        return tabActions.replaceActiveTab({
          pathname: normalizedPath,
          search: normalizedSearch,
          label,
        });
      }

      navigate(buildTabLocation(normalizedPath, normalizedSearch), {
        replace: true,
      });
      return null;
    },
    [enabled, navigate, tabActions]
  );

  const openDetail = useCallback(
    ({ pathname, id, mode = "edit", label }) => {
      if (!id) {
        return open({
          pathname,
          search: mode === "add" ? "?mode=add" : "",
          label,
        });
      }
      return open({
        pathname,
        search: `?id=${id}&mode=${mode}`,
        label,
      });
    },
    [open]
  );

  const closeActive = useCallback(
    (options) => {
      if (enabled) {
        const activeTabId = tabActions.getActiveTabId();
        if (activeTabId) {
          tabActions.closeTab(activeTabId, options);
          return;
        }
      }
      navigate(-1);
    },
    [enabled, navigate, tabActions]
  );

  const goLogin = useCallback(
    (path = "/login") => {
      navigate(path);
    },
    [navigate]
  );

  return {
    enabled,
    open,
    replace,
    openDetail,
    closeActive,
    goLogin,
    navigate: open,
  };
}
